import { useCallback, useEffect, useRef, useState } from 'react';

import WebHIDBarcodeScanner from '@point-of-sale/webhid-barcode-scanner';
import WebSerialBarcodeScanner from '@point-of-sale/webserial-barcode-scanner';

export type BarcodeScannerTransport = 'hid' | 'serial';
export type BarcodeScannerState = 'idle' | 'connected' | 'unsupported';

export interface BarcodeScannerStatus {
  transport: BarcodeScannerTransport | null;
  state: BarcodeScannerState;
}

export interface BarcodeScannerDelivery {
  value: string;
  transport: BarcodeScannerTransport;
}

interface BarcodeScannerSubscriber {
  onBarcode: (delivery: BarcodeScannerDelivery) => void;
  onStatus: (status: BarcodeScannerStatus) => void;
}

// The Honeywell N6600 serial default; adjustable in the scan modal for
// scanners configured differently (persisted per workstation).
export const SERIAL_BAUD_RATES = [9600, 19200, 38400, 57600, 115200];
const DEFAULT_SERIAL_BAUD_RATE = 115200;
const SERIAL_DEVICE_STORAGE_KEY = 'scanner-serial-device';
const SERIAL_BAUD_STORAGE_KEY = 'scanner-serial-baud';
const CONNECT_IN_FLIGHT_BACKSTOP_MS = 30_000;

// WebHID and Web Serial exist only in secure contexts on Chromium browsers.
const isSecureBrowserContext =
  typeof window !== 'undefined' && window.isSecureContext;
export const isHidScannerSupported =
  isSecureBrowserContext && 'hid' in navigator;
export const isSerialScannerSupported =
  isSecureBrowserContext && 'serial' in navigator;

// Both libraries only expose addEventListener (no removal), so single
// page-level instances dispatch to the currently mounted subscribers.
let hidScanner: WebHIDBarcodeScanner | null = null;
let serialScanner: WebSerialBarcodeScanner | null = null;
let hidConnectInFlight = false;
let serialConnectInFlight = false;
// Bumped when the serial instance is swapped (baud change). The library
// defers event callbacks and offers no listener removal, so listeners of a
// discarded instance check their generation and ignore stale events.
let serialGeneration = 0;

// Per-transport states, merged on read — a HID disconnect/unsupported event
// must never mask an active serial connection (and vice versa).
const transportStates: Record<BarcodeScannerTransport, BarcodeScannerState> = {
  hid: 'idle',
  serial: 'idle',
};

const subscribers = new Set<BarcodeScannerSubscriber>();
const serialConnectedResolvers = new Set<() => void>();

// The serial library's connect() does not settle after a successful open
// (its open() awaits the port read loop) — only cancel/failure settles it.
// Success is signalled by the 'connected' event instead.
const waitForSerialConnected = (timeoutMs: number): Promise<void> =>
  new Promise((resolve) => {
    if (transportStates.serial === 'connected') {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      serialConnectedResolvers.delete(done);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    serialConnectedResolvers.add(done);
  });

const deriveStatus = (): BarcodeScannerStatus => {
  if (transportStates.serial === 'connected') {
    return { transport: 'serial', state: 'connected' };
  }
  if (transportStates.hid === 'connected') {
    return { transport: 'hid', state: 'connected' };
  }
  if (transportStates.hid === 'unsupported') {
    return { transport: 'hid', state: 'unsupported' };
  }
  return { transport: null, state: 'idle' };
};

const publishStatus = (
  transport: BarcodeScannerTransport,
  state: BarcodeScannerState,
) => {
  transportStates[transport] = state;
  const status = deriveStatus();
  subscribers.forEach((subscriber) => subscriber.onStatus(status));
};

const dispatchBarcode = (value: string, transport: BarcodeScannerTransport) => {
  subscribers.forEach((subscriber) =>
    subscriber.onBarcode({ value, transport }),
  );
};

const readStoredSerialBaudRate = (): number => {
  try {
    const raw = localStorage.getItem(SERIAL_BAUD_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    if (SERIAL_BAUD_RATES.includes(parsed)) {
      return parsed;
    }
  } catch {
    // Storage unavailable — fall through to the default.
  }
  return DEFAULT_SERIAL_BAUD_RATE;
};

const readStoredSerialDevice = (): {
  vendorId: number;
  productId: number;
} | null => {
  try {
    const raw = localStorage.getItem(SERIAL_DEVICE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      Number.isFinite(parsed?.vendorId) &&
      Number.isFinite(parsed?.productId)
    ) {
      return { vendorId: parsed.vendorId, productId: parsed.productId };
    }
  } catch {
    // Ignore malformed storage; the user can pair again manually.
  }
  return null;
};

const getHidScanner = (): WebHIDBarcodeScanner | null => {
  if (!isHidScannerSupported) return null;

  if (!hidScanner) {
    hidScanner = new WebHIDBarcodeScanner();
    hidScanner.addEventListener('barcode', (event) => {
      dispatchBarcode(event.value, 'hid');
    });
    hidScanner.addEventListener('connected', () =>
      publishStatus('hid', 'connected'),
    );
    hidScanner.addEventListener('disconnected', () =>
      publishStatus('hid', 'idle'),
    );
    hidScanner.addEventListener('unsupported', (event) => {
      // The library also emits 'unsupported' when the chooser returned no
      // devices (e.g. the user cancelled) — that is not a mode problem.
      publishStatus('hid', event?.devices?.length ? 'unsupported' : 'idle');
    });

    // The empty object is required: the library dereferences the argument.
    hidScanner.reconnect({}).catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('HID POS scanner reconnect failed.', error);
    });
  }

  return hidScanner;
};

const getSerialScanner = (): WebSerialBarcodeScanner | null => {
  if (!isSerialScannerSupported) return null;

  if (!serialScanner) {
    serialScanner = new WebSerialBarcodeScanner({
      baudRate: readStoredSerialBaudRate(),
    });
    const generation = serialGeneration;
    serialScanner.addEventListener('barcode', (event) => {
      if (generation !== serialGeneration) return;
      dispatchBarcode(event.value, 'serial');
    });
    serialScanner.addEventListener('connected', (event) => {
      if (generation !== serialGeneration) return;
      publishStatus('serial', 'connected');
      serialConnectedResolvers.forEach((resolveConnected) =>
        resolveConnected(),
      );
      // Persist the ids so the port re-attaches silently on the next page
      // load — the library's reconnect() is a no-op without both ids.
      if (
        Number.isFinite(event?.vendorId) &&
        Number.isFinite(event?.productId)
      ) {
        try {
          localStorage.setItem(
            SERIAL_DEVICE_STORAGE_KEY,
            JSON.stringify({
              vendorId: event.vendorId,
              productId: event.productId,
            }),
          );
        } catch {
          // Storage unavailable — pairing still works for this session.
        }
      }
    });
    serialScanner.addEventListener('disconnected', () => {
      if (generation !== serialGeneration) return;
      publishStatus('serial', 'idle');
    });

    const storedDevice = readStoredSerialDevice();
    if (storedDevice) {
      serialScanner.reconnect(storedDevice).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Serial scanner reconnect failed.', error);
      });
    }
  }

  return serialScanner;
};

// Applying a new baud rate requires reopening the port: drop the current
// instance (closing its port) and recreate it, which silently re-attaches
// the previously paired device at the new rate.
const applySerialBaudRate = async (baudRate: number): Promise<void> => {
  try {
    localStorage.setItem(SERIAL_BAUD_STORAGE_KEY, String(baudRate));
  } catch {
    // Storage unavailable — the rate still applies for this session.
  }

  const previous = serialScanner;
  if (!previous) return;

  serialGeneration += 1;
  serialScanner = null;
  publishStatus('serial', 'idle');
  try {
    await previous.disconnect();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Serial scanner disconnect failed.', error);
  }
  getSerialScanner();
};

interface UseBarcodeScannerOptions {
  enabled: boolean;
  onBarcode: (delivery: BarcodeScannerDelivery) => void;
}

export const useBarcodeScanner = ({
  enabled,
  onBarcode,
}: UseBarcodeScannerOptions) => {
  const [status, setStatus] = useState<BarcodeScannerStatus>(deriveStatus);
  const [serialBaudRate, setSerialBaudRateState] = useState<number>(
    readStoredSerialBaudRate,
  );
  const onBarcodeRef = useRef(onBarcode);
  onBarcodeRef.current = onBarcode;

  useEffect(() => {
    if (!enabled || (!isHidScannerSupported && !isSerialScannerSupported)) {
      return undefined;
    }

    getHidScanner();
    getSerialScanner();

    const subscriber: BarcodeScannerSubscriber = {
      onBarcode: (delivery) => onBarcodeRef.current(delivery),
      onStatus: setStatus,
    };
    subscribers.add(subscriber);
    setStatus(deriveStatus());

    return () => {
      subscribers.delete(subscriber);
    };
  }, [enabled]);

  const connectHid = useCallback(async () => {
    const instance = getHidScanner();
    if (!instance || hidConnectInFlight) return;

    hidConnectInFlight = true;
    // Backstop: if the port/device open hangs and connect() never settles,
    // release the guard so pairing stays retryable without a page reload (a
    // second overlapping chooser request is rejected by the browser and
    // lands in the catch below).
    const backstop = setTimeout(() => {
      hidConnectInFlight = false;
    }, CONNECT_IN_FLIGHT_BACKSTOP_MS);
    try {
      // User gesture required; opens the browser HID chooser. Status changes
      // are event-derived — this await is only a completion/safety boundary.
      await instance.connect();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('HID POS scanner connect failed.', error);
    } finally {
      clearTimeout(backstop);
      hidConnectInFlight = false;
    }
  }, []);

  const connectSerial = useCallback(async () => {
    const instance = getSerialScanner();
    if (!instance || serialConnectInFlight) return;

    serialConnectInFlight = true;
    try {
      // User gesture required; opens the browser serial-port chooser. The
      // library's connect() settles on cancel/failure but NOT on success
      // (its open() awaits the read loop), so race it against the
      // 'connected' event with a backstop timeout.
      await Promise.race([
        instance.connect(),
        waitForSerialConnected(CONNECT_IN_FLIGHT_BACKSTOP_MS),
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Serial scanner connect failed.', error);
    } finally {
      serialConnectInFlight = false;
    }
  }, []);

  const setSerialBaudRate = useCallback(async (baudRate: number) => {
    // Ignore while a serial connect is in flight — swapping the instance
    // mid-chooser would orphan the port the pending connect may still open.
    if (!SERIAL_BAUD_RATES.includes(baudRate) || serialConnectInFlight) return;
    setSerialBaudRateState(baudRate);
    await applySerialBaudRate(baudRate);
  }, []);

  return {
    isHidSupported: isHidScannerSupported,
    isSerialSupported: isSerialScannerSupported,
    status,
    connectHid,
    connectSerial,
    serialBaudRate,
    setSerialBaudRate,
  };
};
